import type { DoanhThuNhieuKenh } from '@qlk/shared';
import { ngayDayVn, ngayGonVn } from '@qlk/shared';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState, Skeleton } from '../../../../ui/index.js';
import { phanTram } from '../dinhDang.js';
import { dinhDangChiSo } from './dinhDangChiSo.js';
import { KhoiGop, ThanhNgang } from './KhoiGop.js';
import { ThanhBonO } from './ThanhBonO.js';

/**
 * Tab "Doanh thu" của trang Hiệu suất.
 *
 * Bốn ô, biểu đồ cột theo ngày, rồi hàng hai khung (theo kênh · theo nguồn
 * tiền) và một link sang trang Doanh thu.
 *
 * Tab này chỉ hiện khi có `revenue:view`, và endpoint cũng chặn — ẩn ở giao
 * diện là cho gọn, không phải để bảo mật.
 */

export interface TabDoanhThuProps {
  du: DoanhThuNhieuKenh | undefined;
  dangTai: boolean;
  loi: string | null;
  maKy: string;
}

export function TabDoanhThu({ du, dangTai, loi, maKy }: TabDoanhThuProps) {
  if (loi !== null) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState loi title="Không tải được số liệu doanh thu" description={loi} />
      </div>
    );
  }

  const trongPhamVi = du?.soKenhTrongPhamVi ?? 0;
  const currency = du?.currency ?? 'USD';

  return (
    <div className="flex flex-col gap-4">
      <ThanhBonO o={du?.o ?? []} dangTai={dangTai} currency={currency} />

      <section className="rounded-the border border-vien bg-be-mat p-6">
        <h2 className="text-tieu-de-khoi font-semibold text-chu">Doanh thu gộp theo ngày</h2>
        <p className="mt-1 text-phu text-chu-mo">
          Trước thuế Mỹ, phần Network và VAT — số gốc của YouTube.
        </p>
        <div className="mt-4">
          <CotDoanhThu du={du} dangTai={dangTai} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-[3fr_2fr]">
        <KhoiGop
          tieuDe="Doanh thu theo kênh"
          soKenhCoSoLieu={du?.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={(du?.theoKenh ?? []).length === 0}
          caoKhungXuong="16rem"
        >
          <TheoKenh du={du} maKy={maKy} currency={currency} />
        </KhoiGop>

        <KhoiGop
          tieuDe="Theo nguồn tiền"
          soKenhCoSoLieu={du?.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={(du?.theoNguonTien ?? []).length === 0}
          caoKhungXuong="16rem"
        >
          <div className="flex h-full flex-col">
            <ul className="flex flex-col gap-4">
              {(du?.theoNguonTien ?? []).map((n) => (
                <li key={n.id}>
                  <div className="flex items-baseline justify-between gap-3 text-co-ban">
                    <span className="truncate text-chu">{n.nhan}</span>
                    <span className="shrink-0 whitespace-nowrap">
                      <span className="so-lieu text-bang text-chu-mo">
                        {dinhDangChiSo(n.gross, 'tien', currency)}
                      </span>
                      <span className="so-lieu ml-2 font-semibold text-chu">
                        {phanTram(n.phanTram)}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2">
                    <ThanhNgang
                      phanTram={n.phanTram}
                      mau={n.loai === 'adsense' ? 'var(--color-do-tien)' : 'var(--color-nhan)'}
                    />
                  </div>
                </li>
              ))}
            </ul>

            {/* `mt-auto` để link nằm ở chân thẻ, không lơ lửng giữa khung. */}
            <p className="mt-auto pt-4">
              <Link
                to={`/doanh-thu?ky=${encodeURIComponent(maKy)}`}
                className="inline-flex items-center gap-1.5 text-co-ban font-semibold text-nhan hover:underline"
              >
                Dòng tiền chi tiết ở trang Doanh thu
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </p>
          </div>
        </KhoiGop>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Biểu đồ cột                                                         */
/* ------------------------------------------------------------------ */

const RONG = 1000;
const CAO = 200;
const LE_PHAI = 64;
const LE_DUOI = 24;
const LE_TREN = 4;

/** Trần trục: 10 % trên giá trị lớn nhất, như biểu đồ của tab Tổng quan. */
const HE_SO_TRAN = 1.1;

/**
 * Cột doanh thu theo ngày — SVG thuần (quyết định chốt số 5).
 *
 * **Cột** chứ không đường: doanh thu từng ngày là những khoản rời nhau, nối
 * chúng bằng một đường là ngụ ý một dòng chảy liên tục không có thật.
 *
 * Khoảng `giaTri === null` **không vẽ cột nào** — đó là "chưa có doanh thu khả
 * dụng", không phải 0 $. Vẽ cột cao 0 ở đó là nói kênh không kiếm được gì.
 *
 * Độ chi tiết do máy chủ chọn (`doChiTietCuaKy`), cùng luật với mọi biểu đồ của
 * app: ≤ 90 ngày theo ngày, ≤ 2 năm theo tuần, dài hơn theo tháng.
 */
function CotDoanhThu({ du, dangTai }: { du: DoanhThuNhieuKenh | undefined; dangTai: boolean }) {
  if (dangTai) return <Skeleton cao="200px" />;

  const cot = du?.cot ?? [];
  const coSo = cot.filter((c) => c.giaTri !== null);

  if (coSo.length === 0) {
    return <EmptyState title="Chưa có doanh thu trong kỳ này" />;
  }

  const currency = du?.currency ?? 'USD';
  const tran = Math.max(...coSo.map((c) => c.giaTri ?? 0), 1) * HE_SO_TRAN;
  const day = CAO - LE_DUOI;

  /* Bề ngang cột: chia đều chỗ, chừa 20 % làm khe. */
  const buoc = (RONG - LE_PHAI) / Math.max(cot.length, 1);
  const beCot = Math.max(buoc * 0.8, 1);

  const vach = [0, 0.25, 0.5, 0.75, 1];
  const yCua = (v: number) => LE_TREN + (1 - Math.min(v / tran, 1)) * (day - LE_TREN);

  return (
    <>
      <svg
        viewBox={`0 0 ${String(RONG)} ${String(CAO + 16)}`}
        className="h-[200px] w-full"
        role="img"
        aria-label="Biểu đồ doanh thu gộp theo ngày"
      >
        {vach.map((v) => {
          const y = yCua(tran * v);
          return (
            <g key={v}>
              <line
                x1={0}
                x2={RONG - LE_PHAI}
                y1={y}
                y2={y}
                stroke="var(--color-vien)"
                strokeWidth={1}
              />
              <text
                x={RONG - LE_PHAI + 8}
                y={y + 4}
                className="so-lieu"
                fontSize={13}
                fill="var(--color-chu-phu)"
              >
                {dinhDangChiSo(tran * v, 'tien', currency)}
              </text>
            </g>
          );
        })}

        {cot.map((c, i) => {
          if (c.giaTri === null) return null;
          const y = yCua(c.giaTri);
          return (
            <rect
              key={c.date}
              x={i * buoc + (buoc - beCot) / 2}
              y={y}
              width={beCot}
              height={Math.max(day - y, 1)}
              rx={2}
              /* Khoảng chưa chốt tô xám: số của nó còn đổi. */
              fill={c.daChot ? 'var(--color-nhan)' : 'var(--color-do-trong)'}
            >
              <title>{`${ngayDayVn(c.date)} · ${dinhDangChiSo(c.giaTri, 'tien', currency)}`}</title>
            </rect>
          );
        })}

        {[...new Set([0, Math.floor(cot.length / 2), cot.length - 1])].map((i) => {
          const c = cot[i];
          if (!c) return null;
          return (
            <text
              key={`truc-${c.date}`}
              x={Math.min(Math.max(i * buoc + buoc / 2, 24), RONG - LE_PHAI - 24)}
              y={CAO + 12}
              fontSize={13}
              fill="var(--color-chu-phu)"
              textAnchor="middle"
            >
              {ngayGonVn(c.date)}
            </text>
          );
        })}
      </svg>

      {/* Bảng chữ cho trình đọc màn hình: SVG không đọc được số liệu. */}
      <div className="sr-only">
        <table>
          <caption>Doanh thu gộp theo ngày</caption>
          <thead>
            <tr>
              <th scope="col">Ngày</th>
              <th scope="col">Doanh thu gộp</th>
            </tr>
          </thead>
          <tbody>
            {cot.map((c) => (
              <tr key={c.date}>
                <th scope="row">{ngayDayVn(c.date)}</th>
                <td>{dinhDangChiSo(c.giaTri, 'tien', currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Doanh thu theo kênh                                                 */
/* ------------------------------------------------------------------ */

/** Năm cột của cả bảng, khai một lần trên `<ul>` — xem `SoSanhKenh`. */
const LUOI_BANG = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-x-3 gap-y-4';
const LUOI_DONG = 'col-span-4 grid grid-cols-subgrid items-center';

function TheoKenh({
  du,
  maKy,
  currency,
}: {
  du: DoanhThuNhieuKenh | undefined;
  maKy: string;
  currency: string;
}) {
  const dong = du?.theoKenh ?? [];
  const lonNhat = Math.max(...dong.map((d) => d.gross), 1);

  return (
    <ul className={LUOI_BANG}>
      {dong.map((d) => (
        <li key={d.channelId} data-dong="doanh-thu-kenh" className={LUOI_DONG}>
          <span className="min-w-0">
            <Link
              to={`/kenh/${d.channelId}?ky=${encodeURIComponent(maKy)}`}
              className="block truncate text-co-ban font-semibold text-chu hover:text-nhan hover:underline"
            >
              {d.ten}
            </Link>
            <span className="block truncate text-bang text-chu-mo">
              {d.network ?? 'AdSense trực tiếp'}
            </span>
          </span>

          <ThanhNgang phanTram={(d.gross / lonNhat) * 100} />

          <span className="whitespace-nowrap text-right">
            <span className="so-lieu block text-co-ban font-semibold text-chu">
              {dinhDangChiSo(d.gross, 'tien', currency)}
            </span>
            {/* `null` = chưa đủ ngày có cả doanh thu và lượt xem ⇒ `—`. */}
            <span className="so-lieu block text-bang text-chu-mo">
              {d.rpm === null ? '—' : `RPM ${dinhDangChiSo(d.rpm, 'rpm', currency)}`}
            </span>
          </span>

          <span className="justify-self-end">
            {d.pct === null ? (
              <span className="so-lieu text-bang text-chu-mo">—</span>
            ) : (
              <span
                data-huong={d.pct >= 0 ? 'tang' : 'giam'}
                className={`so-lieu inline-block whitespace-nowrap rounded-chip px-2 py-0.5 text-bang font-semibold ${
                  d.pct >= 0 ? 'bg-ok-nen text-tang' : 'bg-bad-nen text-giam'
                }`}
              >
                {d.pct >= 0 ? '▲' : '▼'} {phanTram(Math.abs(d.pct))}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
